import {
  useState,
  useCallback,
  useMemo,
  startTransition,
  useEffect,
} from 'react'
import { useDebounce } from 'use-debounce'

import { getUrlParams, setUrlParams } from '@/utils/browser'
import {
  getSortFunction,
  getInitialSortValue,
  isSupportedSortOption,
  getFilteringMethods,
  getInitialFilters,
  callFilters,
  checkIfSupportedFilters,
  handleCompatibilityExclusion,
} from '@/utils/products'
import { convertToArray } from '@/utils/func'
import { AMPLITUDE_EVENTS, FILTERS, SEARCH } from '@/constants'
import { logAmplitudeEvent } from '@/store/stats'
import { useTypedDispatch } from '@/store/hooks'
import { getSearchFunction } from '@/utils/search'

const useList = ({
  allItems,
  defaultSortValue,
  defaultFilters,
  defaultSortOptions,
  defaultFiltersOptions,
  supportedFilters,
  section,
}) => {
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query, 150)
  const [items, setApps] = useState(allItems || [])
  const [sort, setSort] = useState(() => getInitialSortValue(defaultSortValue))
  const [sortOptions, setSortOptions] = useState(defaultSortOptions || [])
  const [filters, setFilters] = useState(() =>
    getInitialFilters(defaultFilters, supportedFilters)
  )
  const [filtersOptions, setFiltersOptions] = useState(
    defaultFiltersOptions || {}
  )
  const dispatch = useTypedDispatch()

  // FILTERS:
  const clearFilters = useCallback(() => setFilters({}), [])

  const clearFilter = (id) => {
    const currentFilters = { ...filters }
    delete currentFilters[id]

    setFilters(currentFilters)
  }

  const updateFilters = useCallback(
    (id, value, isMultiselect) => {
      let updatedFilters = filters

      // Handle compatibility session storage exception
      if (id === FILTERS.COMPATIBILITY.ID) {
        handleCompatibilityExclusion({ compatibility: !filters.compatibility })
      }

      // Handle multiselect / multi-value filters
      if (Array.isArray(filters[id]) || isMultiselect) {
        if (filters[id] && filters[id].includes(value)) {
          const filterValue = convertToArray(filters[id])
          const updatedValue = filterValue.filter((v) => v !== value)
          updatedFilters = { ...filters, [id]: updatedValue }
        } else {
          const filterValue = convertToArray(filters[id])
          updatedFilters = { ...filters, [id]: [...filterValue, value] }
        }
        // Handle single value filters
      } else {
        if (filters[id] === value) {
          updatedFilters = { ...filters, [id]: undefined }
        } else {
          updatedFilters = { ...filters, [id]: value }
        }
      }

      updatedFilters = checkIfSupportedFilters(updatedFilters, supportedFilters)

      setFilters(updatedFilters)
    },
    [filters, supportedFilters]
  )

  const filterItems = useCallback(
    (items) => {
      const filteringMethods = getFilteringMethods(filters, filtersOptions)
      return callFilters(filteringMethods, items)
    },
    [filters, filtersOptions]
  )

  // SORT:
  const updateSort = useCallback((value) => {
    setSort(value)
  }, [])

  const sortItems = useCallback(
    (items) => {
      const sortingMethod = getSortFunction(sort)
      const isSupportedSortingValue = isSupportedSortOption(sort, sortOptions)

      return sortingMethod && isSupportedSortingValue
        ? items.sort(sortingMethod)
        : items
    },
    [sort, sortOptions]
  )

  // SEARCH:
  const updateSearch = useCallback((value) => {
    setQuery(value)
  }, [])

  const searchItems = useCallback(
    (items, query) => {
      if (!query) return items

      const searchMethod = getSearchFunction(section)
      const isSearchEnabled = SEARCH.ENABLED_SECTIONS.includes(section)

      return searchMethod && isSearchEnabled
        ? searchMethod(items, query)
        : items
    },
    [section]
  )

  // ITEMS:
  const filterAndSortItems = useCallback(
    (items, query) => {
      const searchedApps = searchItems(items, query)
      const filteredApps = filterItems([...searchedApps])
      const filteredAndSortedApps = sortItems(filteredApps)

      return filteredAndSortedApps
    },
    [filterItems, searchItems, sortItems]
  )

  useMemo(
    () => {
      const updatedApps = filterAndSortItems(allItems, debouncedQuery)
      startTransition(() => {
        setApps(updatedApps)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, sort, allItems.length, debouncedQuery]
  )

  useMemo(() => {
    const currentParams = getUrlParams()
    const supportedParams = Object.keys(currentParams)

    setUrlParams({ ...currentParams, ...filters, sortBy: sort }, [
      ...supportedParams,
      ...supportedFilters,
    ])
  }, [filters, sort, supportedFilters])

  useEffect(() => {
    if (!debouncedQuery) {
      return
    }

    dispatch(
      logAmplitudeEvent({
        name: AMPLITUDE_EVENTS.SEARCH_PERFORMED,
        params: {
          query: debouncedQuery,
        },
      })
    )
  }, [dispatch, debouncedQuery])

  return {
    items,
    filters,
    clearFilters,
    clearFilter,
    updateFilters,
    sort,
    updateSort,
    sortOptions,
    setSortOptions,
    filtersOptions,
    setFiltersOptions,
    query,
    updateSearch,
    section,
  }
}

export default useList
